import { useGetBenchmarkQuery } from '@vizit/api';
import { AlertLevel, Button, ButtonLevel, ButtonSize, useToast } from '@vizit/atoms';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { BenchmarkImageSortOption, isBenchmarkImageScoreSort, useBenchmarkImageUpload } from '@vizit/collections';
import { nameRefusedCategories, nameScorings, parseBenchmarkImageSortOption } from '@vizit/collections';
import { useBenchmarkImages, useFindProductCategory } from '@vizit/collections';
import { useProductCategorySearch, useScoredProductCategories } from '@vizit/collections';
import { useBenchmarkScorings } from '@vizit/collections';
import { BenchmarkBulkActionBar, getOnScreenAssetIds } from '@vizit/collections';
import { useAssetSelection, useOnScreenAssetIds } from '@vizit/collections';
import { BENCHMARK_UNCATEGORIZED_IMAGE_EXTENSIONS } from '@vizit/constants';
import { useShowActionsBar, useShowComparison, useShowFavorites, useShowTagging } from '@vizit/feature-flags';
import { AlertCircleIcon, ArrowNarrowDownIcon, ArrowNarrowUpIcon, ClockRewindIcon } from '@vizit/icons';
import { SortAlphabeticalIcon } from '@vizit/icons';
import { LibraryEmptyState, ThumbnailSizeSlider } from '@vizit/molecules';
import { BenchmarkDetailsHeader, BenchmarkImageGrid, BenchmarkSummaryBar } from '@vizit/organisms';
import { BenchmarkUploadDialog, ClearableSelect, UploadImagesButton } from '@vizit/organisms';
import { AddProductCategoriesDialog, BenchmarkImagePreviewDialog, ProductCategoryPicker } from '@vizit/organisms';
import { describeUploadQueueError, sortPickerCategories, useThumbnailSlider, useUploadQueue } from '@vizit/organisms';
import { type Heatmap, HeatmapEducationStrip, HeatmapPicker } from '@vizit/organisms';
import type { BenchmarkImagePreview, ClearableSelectItem } from '@vizit/organisms';
import { colors, cx } from '@vizit/theme';
import * as React from 'react';

const SORT_ICON_COLOR = colors['gray-utility']['400'];

const ADD_CATEGORIES_SUBTITLE =
  "Each category you add is a separate shopper audience. Switch between them to see how each one scores this benchmark's images.";

type SortItem = ClearableSelectItem & { id: BenchmarkImageSortOption };

const SORT_ITEMS: SortItem[] = [
  {
    id: BenchmarkImageSortOption.Newest,
    label: 'Newest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkImageSortOption.Oldest,
    label: 'Oldest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkImageSortOption.Name,
    label: 'Name (A-Z)',
    icon: <SortAlphabeticalIcon size={14} className="text-gray-utility-400" aria-hidden />,
  },
  {
    id: BenchmarkImageSortOption.ScoreDesc,
    label: 'Score: high to low',
    icon: <ArrowNarrowDownIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkImageSortOption.ScoreAsc,
    label: 'Score: low to high',
    icon: <ArrowNarrowUpIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
];

// Scores are the benchmark's under a product category, so without one there is nothing to sort by.
const DATE_AND_NAME_SORT_ITEMS = SORT_ITEMS.filter((item) => !isBenchmarkImageScoreSort(item.id));

interface BenchmarkDetailsPageProps {
  /**
   * The benchmark to show
   */
  benchmarkId: string;
  /**
   * The active workspace
   *
   * @optional
   */
  workspaceId?: string;
  /**
   * How the images are sorted, kept in the URL by the route. A score sort only applies while the selected product
   * category's scoring is ready; otherwise the page sorts newest first and sets the URL to match.
   */
  sortOption: BenchmarkImageSortOption;
  /**
   * Called when the sort changes
   */
  onSortOptionChange: (option: BenchmarkImageSortOption) => void;
  heatmap?: Heatmap;
  onHeatmapChange?: (heatmap: Heatmap) => void;
}

/**
 * A custom benchmark's page: its name, image type and image count, and its images. Once the benchmark is scored
 * under product categories, the header's picker chooses which category's scores the tiles show: a category just
 * added is selected right away and its tiles say "Scoring…" until the scoring lands, while switching between scored
 * categories only swaps the score bars.
 */
function BenchmarkDetailsPage(props: BenchmarkDetailsPageProps) {
  const { benchmarkId, workspaceId, sortOption, onSortOptionChange, onHeatmapChange } = props;
  const showComparison = useShowComparison();
  const showTagging = useShowTagging();
  const showActionsBar = useShowActionsBar();
  const heatmap = showComparison ? (props.heatmap ?? 'original') : 'original';
  const showFavorites = useShowFavorites();

  const benchmarkQuery = useGetBenchmarkQuery(
    { workspace_id: workspaceId ?? '', benchmark_id: benchmarkId },
    { enabled: !!workspaceId && !!benchmarkId },
  );
  const benchmark = benchmarkQuery.data;

  const { toUploadQueueItems, uploadingImageIds, failedImageIds, uploadLandedAt, isRefreshing } =
    useBenchmarkImageUpload({
      workspaceId,
      benchmarkId,
    });

  const {
    scored,
    processing,
    failed,
    progress,
    lastSelected,
    isResolved: areScoringsResolved,
    requestScorings,
    recordSelection: saveSelection,
  } = useBenchmarkScorings({ workspaceId, benchmarkId, uploadLandedAt });
  // Exactly what the picker lists; a scoring in no list cannot be the selection.
  const listedScorings = React.useMemo(() => [...scored, ...processing, ...failed], [scored, processing, failed]);
  const hasCategories = listedScorings.length > 0;

  // `undefined` means the user has not picked yet, so the page follows the category last selected on the server.
  const [pickedCategoryId, setPickedCategoryId] = React.useState<string | null | undefined>(undefined);
  const [isAddCategoriesOpen, setIsAddCategoriesOpen] = React.useState(false);
  const [addCategoriesError, setAddCategoriesError] = React.useState<string | null>(null);
  // The listed categories name and color the picker rows and give their paths; the dialog searches its own list, the
  // ones this benchmark can score.
  const { byId: categoryById, status: categoryPathsStatus } = useScoredProductCategories({
    workspaceId,
    scorings: listedScorings,
  });
  const scorableFilters = React.useMemo(() => ({ scorable_for_benchmark_id: benchmarkId }), [benchmarkId]);
  const categorySearch = useProductCategorySearch({
    workspaceId,
    filters: scorableFilters,
    enabled: isAddCategoriesOpen,
  });

  const findCategory = useFindProductCategory(categoryById, categorySearch.categories);

  // The inline status names the selection as its picker row reads.
  const { toPicker, nameOf } = React.useMemo(
    () => nameScorings(listedScorings, findCategory),
    [listedScorings, findCategory],
  );
  const excludedCategoryIds = React.useMemo(
    () => [...scored, ...processing].map((scoring) => scoring.productCategoryId),
    [scored, processing],
  );
  const pickerRows = React.useMemo(
    () => ({ scored: scored.map(toPicker), processing: processing.map(toPicker), failed: failed.map(toPicker) }),
    [scored, processing, failed, toPicker],
  );
  // "None selected" shows the images without scores; once a category is scored, one always is selected: the one
  // picked or last selected while it is still listed, else the picker's first scored row.
  const allowNone = scored.length === 0;
  const chosenCategoryId = pickedCategoryId === undefined ? lastSelected?.productCategoryId : pickedCategoryId;
  const isChosenListed = listedScorings.some((scoring) => scoring.productCategoryId === chosenCategoryId);
  const selectedCategoryId = isChosenListed
    ? (chosenCategoryId ?? null)
    : (sortPickerCategories(pickerRows.scored)[0]?.id ?? null);
  const selectedScoring = listedScorings.find((scoring) => scoring.productCategoryId === selectedCategoryId) ?? null;
  const isFallbackSelection = !!selectedScoring && selectedCategoryId !== chosenCategoryId;
  const selectedName = selectedScoring ? nameOf(selectedScoring) : null;
  const selectedProgress = selectedScoring ? progress.get(selectedScoring.productCategoryId) : undefined;
  // "None selected", or nothing scored yet, scores nothing.
  const scoreCategoryId = selectedScoring?.productCategoryId ?? null;
  // A pending scoring has no scores yet: sorted by them, the images would come back in ID order and reshuffle when
  // the scores land. So the score sorts wait for a ready one, and the API refuses them without a category at all.
  const canSortByScore = selectedScoring?.status === 'ready';
  const appliedSortOption =
    isBenchmarkImageScoreSort(sortOption) && !canSortByScore ? BenchmarkImageSortOption.Newest : sortOption;
  const scoringCategoryName = selectedScoring?.status === 'pending' ? selectedName : null;
  const failedScoring =
    selectedScoring?.status === 'failed' && selectedName
      ? { categoryName: selectedName, reason: selectedProgress?.failureReason }
      : null;

  const {
    images,
    items,
    totalCount,
    processingCount,
    isLoading: isImagesLoading,
    hasNextPage,
    isFetchingNextPage,
    nextPageSize,
    loadMoreRef,
  } = useBenchmarkImages({
    workspaceId,
    benchmarkId,
    sortOption: appliedSortOption,
    productCategoryId: scoreCategoryId,
    // Until the scorings are known the category is not, and reading the list without one would be a wasted read.
    isReady: areScoringsResolved,
    uploadingImageIds,
    failedImageIds,
    isAwaitingUploads: isRefreshing,
    heatmap,
    withArtifacts: showComparison,
    withTags: showTagging,
  });

  // The list's total follows uploads as they land; the benchmark's own count only updates when it is refetched.
  const imageCount = isImagesLoading ? (benchmark?.imageCount ?? 0) : totalCount;
  const imagesNeeded = Math.max((benchmark?.minimumImages ?? 0) - imageCount, 0);
  // Under its minimum the API refuses to record a pick (400), so a category switch or fallback stays on the page. The
  // minimum is only known once the benchmark loads, so nothing is recorded before then either.
  const canRecordSelection = !!benchmark && imagesNeeded === 0;
  const recordSelection = React.useCallback<typeof saveSelection>(
    (...args) => {
      if (canRecordSelection) {
        saveSelection(...args);
      }
    },
    [canRecordSelection, saveSelection],
  );

  const selectableIds = React.useMemo(
    () => items.filter((item) => item.status === 'ready').map((item) => item.id),
    [items],
  );
  const selection = useAssetSelection({
    orderedIds: selectableIds,
    resetKey: [benchmarkId, sortOption, selectedScoring ? selectedCategoryId : ''].join('|'),
  });
  const selectedImages = images.filter((image) => selection.selectedIds.has(image.id));
  const isSelectionMode = showActionsBar && selectedImages.length > 0;
  const gridRef = React.useRef<HTMLDivElement>(null);
  const onScreenIds = useOnScreenAssetIds(gridRef, { isEnabled: isSelectionMode, contentKey: selectableIds });
  const thumbnailSlider = useThumbnailSlider('benchmark', { isEnabled: showActionsBar, isHidden: isSelectionMode });

  // The benchmark reports how many collections are scored against it. "Upload images" stays disabled until the
  // benchmark is known, so the dialog never opens before this count is.
  const collectionCount = benchmark?.collectionCount ?? 0;
  const { onAddFiles } = useUploadQueue();
  const { addToast } = useToast();

  const [isUploadOpen, setIsUploadOpen] = React.useState(false);
  const [previewImageId, setPreviewImageId] = React.useState<string | null>(null);

  // The URL is the sort's source of truth, so a score sort that cannot apply (an old link, a cleared or still scoring
  // category) is replaced there too; left in, it would come back with the next ready category. Until the scorings
  // resolve the category is unknown, so the URL waits.
  React.useEffect(() => {
    if (areScoringsResolved && appliedSortOption !== sortOption) {
      onSortOptionChange(appliedSortOption);
    }
  }, [areScoringsResolved, appliedSortOption, sortOption, onSortOptionChange]);

  // A selection the page fell back to is recorded once, so it stays put when another category lands later and a
  // reload reopens there.
  const recordedFallbackRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Not marked as recorded while nothing can be sent, so it records once the benchmark can score again.
    if (!isFallbackSelection || !areScoringsResolved || !selectedScoring || !canRecordSelection) {
      return;
    }
    if (recordedFallbackRef.current === selectedScoring.productCategoryId) {
      return;
    }
    recordedFallbackRef.current = selectedScoring.productCategoryId;
    recordSelection(selectedScoring.productCategoryId);
  }, [isFallbackSelection, areScoringsResolved, selectedScoring, canRecordSelection, recordSelection]);

  const openAddCategories = React.useCallback(() => {
    setAddCategoriesError(null);
    setIsAddCategoriesOpen(true);
  }, []);

  const handleSelectCategory = (categoryId: string | null) => {
    setPickedCategoryId(categoryId);
    if (categoryId) {
      recordSelection(categoryId);
    }
  };

  const handleAddCategories = async (categoryIds: string[]) => {
    setAddCategoriesError(null);
    const { requested, failedCategoryIds } = await requestScorings(categoryIds);
    // The first category that got through becomes the selection, scoring or already scored, and is recorded as
    // such: every request stamps a selection, so without this a reload would reopen on whichever landed last.
    const first = categoryIds.map((id) => requested.find((scoring) => scoring.productCategoryId === id)).find(Boolean);
    if (first) {
      setPickedCategoryId(first.productCategoryId);
      recordSelection(first.productCategoryId, first);
    }
    if (failedCategoryIds.length > 0) {
      const names = nameRefusedCategories(failedCategoryIds, {
        listed: categorySearch.categories,
        excludedIds: excludedCategoryIds,
        findCategory,
      }).map((name) => name ?? 'a category');
      setAddCategoriesError(`Couldn't add ${names.join(', ')}. Try again.`);
    }
    return { failedCategoryIds };
  };

  const previewImage = images.find((image) => image.id === previewImageId);
  const preview: BenchmarkImagePreview | null = previewImage
    ? {
        name: previewImage.name,
        imageUrl: previewImage.largeThumbnailUrl ?? previewImage.originalUrl,
        fallbackImageUrl: previewImage.originalUrl,
        // The benchmark's score under a product category; null until the benchmark has one.
        score: previewImage.vizitScore,
        // Hero benchmarks hold hero images only; carousel images show their class, never "Carousel".
        assetClass: benchmark?.scope === 'hero' ? 'Hero' : (previewImage.classification ?? 'Unclassified'),
      }
    : null;
  const openUpload = React.useCallback(() => setIsUploadOpen(true), []);

  const handleUpload = (files: File[]) => {
    // The API starts rescoring the collections once the first file lands, but cannot say when it is done, so the
    // toast says what the upload set in motion. Once it can, this becomes "All collections have been rescored".
    const items = toUploadQueueItems(files, {
      onFirstLanded: () => {
        // The dialog only submits once a rescore of these is acknowledged; the API rescores them either way.
        if (collectionCount > 0) {
          addToast({
            title: 'Rescoring collections',
            description:
              collectionCount === 1
                ? 'The collection scored against this benchmark is being rescored with the new images.'
                : 'The collections scored against this benchmark are being rescored with the new images.',
            level: AlertLevel.Info,
          });
        }
      },
    });
    try {
      onAddFiles(items);
    } catch (error) {
      addToast({ ...describeUploadQueueError(error), level: AlertLevel.Error });
    }
  };

  if (benchmarkQuery.isError) {
    return (
      <div className="flex flex-col px-8 pt-12 pb-8 max-sm:px-4 max-sm:pt-8">
        <LibraryEmptyState
          heading="Couldn't load this benchmark"
          description="It may have been deleted, or something went wrong. Try again in a moment."
          icon={AlertCircleIcon}
        >
          <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => benchmarkQuery.refetch()}>
            Try again
          </Button>
        </LibraryEmptyState>
      </div>
    );
  }

  const hasImages = imageCount > 0;
  const canAddCategories = imagesNeeded === 0;

  const uploadButton = <UploadImagesButton onPress={openUpload} isDisabled={!benchmark} />;

  return (
    <div className="flex flex-col gap-8 px-8 pt-12 pb-8 max-sm:px-4 max-sm:pt-8">
      <BenchmarkDetailsHeader
        name={benchmark?.name ?? ''}
        description={benchmark?.description}
        imagesNeeded={imagesNeeded}
        isLoading={!benchmark}
      >
        {hasImages && (
          // The view controls row; Attention maps joins Sort here, on its left.
          <div className="flex flex-wrap items-center justify-end gap-8">
            {showComparison && onHeatmapChange && (
              <div className="flex items-center gap-4">
                <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
                  Heat maps:
                </Text>
                <HeatmapPicker heatmap={heatmap} onHeatmapChange={onHeatmapChange} className="w-[190px]" />
              </div>
            )}
            <div className="flex items-center gap-4">
              <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
                Sort by:
              </Text>
              <ClearableSelect
                ariaLabel="Sort images"
                placeholder="Newest"
                items={canSortByScore ? SORT_ITEMS : DATE_AND_NAME_SORT_ITEMS}
                selectedKey={appliedSortOption}
                onSelectionChange={(key) => key && onSortOptionChange(parseBenchmarkImageSortOption(key))}
                includeAllOption={false}
                isClearable={false}
                className="w-[190px]"
              />
            </div>
          </div>
        )}
        <BenchmarkSummaryBar
          imageCount={imageCount}
          minimumImages={benchmark?.minimumImages ?? 0}
          isScored={selectedScoring?.status === 'ready'}
          scope={benchmark?.scope ?? 'carousel'}
          processingCount={processingCount}
          scoringCategoryName={scoringCategoryName}
          scoringProgress={
            selectedProgress?.status === 'pending'
              ? { scored: selectedProgress.scoredImages, total: selectedProgress.totalImages }
              : null
          }
          failedScoring={failedScoring}
          onAddProductCategory={openAddCategories}
          productCategoryControl={
            hasCategories ? (
              <ProductCategoryPicker
                scoredCategories={pickerRows.scored}
                processingCategories={pickerRows.processing}
                failedCategories={pickerRows.failed}
                pathsStatus={categoryPathsStatus}
                allowNone={allowNone}
                selectedCategoryId={scoreCategoryId}
                onSelectionChange={handleSelectCategory}
                // Under the minimum the API refuses a new scoring, so the picker offers no add row until then.
                onAddCategory={canAddCategories ? openAddCategories : undefined}
                appearance="field"
              />
            ) : undefined
          }
          actions={uploadButton}
        />
      </BenchmarkDetailsHeader>

      {hasImages && <HeatmapEducationStrip heatmap={heatmap} />}

      <div ref={gridRef}>
        <BenchmarkImageGrid
          images={items}
          totalCount={totalCount}
          minimumImages={benchmark?.minimumImages ?? 0}
          isLoading={isImagesLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          nextPageSize={nextPageSize}
          loadMoreRef={loadMoreRef}
          emptyStateAction={uploadButton}
          onImagePress={setPreviewImageId}
          columns={thumbnailSlider.gridColumns}
          className={cx((thumbnailSlider.isVisible || isSelectionMode) && 'pb-36')}
          selection={
            showActionsBar
              ? {
                  isSelected: selection.isSelected,
                  isSelectionMode,
                  onToggle: (id, { shiftKey }) => selection.toggle(id, { shiftKey }),
                }
              : undefined
          }
        />
      </div>

      {isSelectionMode && workspaceId && (
        <BenchmarkBulkActionBar
          workspaceId={workspaceId}
          benchmarkId={benchmarkId}
          selectedImages={selectedImages}
          imageCount={imageCount}
          minimumImages={benchmark?.minimumImages ?? 0}
          collectionCount={collectionCount}
          isOnScreenSelected={selection.isEverySelected(onScreenIds)}
          onSelectVisible={() => selection.selectVisible(getOnScreenAssetIds(gridRef.current))}
          onClear={selection.clear}
          showFavorite={showFavorites}
        />
      )}

      {thumbnailSlider.isVisible && (
        <ThumbnailSizeSlider
          columns={thumbnailSlider.columns}
          onColumnsChange={thumbnailSlider.setColumns}
          className="fixed bottom-12 right-12 z-30"
        />
      )}

      <AddProductCategoriesDialog
        isOpen={isAddCategoriesOpen}
        onOpenChange={setIsAddCategoriesOpen}
        subtitle={ADD_CATEGORIES_SUBTITLE}
        categories={categorySearch.categories}
        totalCount={categorySearch.totalCount}
        onSearchChange={categorySearch.setSearch}
        // Failed ones stay addable, which is how a timed-out scoring is requested again.
        excludedCategoryIds={excludedCategoryIds}
        isLoading={categorySearch.isLoading}
        isSearching={categorySearch.isSearching}
        isScorableOnly
        isLoadError={categorySearch.isError}
        onRetry={categorySearch.retry}
        errorMessage={addCategoriesError}
        onAdd={handleAddCategories}
      />

      <BenchmarkImagePreviewDialog image={preview} onOpenChange={(isOpen) => !isOpen && setPreviewImageId(null)} />

      <BenchmarkUploadDialog
        isOpen={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        rescoreCollectionCount={collectionCount}
        // The benchmark has no product category yet, so the API reads each image's format from its file name.
        allowedExtensions={BENCHMARK_UNCATEGORIZED_IMAGE_EXTENSIONS}
        onSubmit={handleUpload}
      />
    </div>
  );
}

export { BenchmarkDetailsPage };
export type { BenchmarkDetailsPageProps };
